/**
 * Tipos manuais ate o `pnpm gen:api` gerar schema.ts a partir do
 * /openapi.json. Quando o gen rodar, esses tipos devem casar com o
 * que o backend define em src/api/schemas/*.py.
 */

export type Role = "manager" | "creator" | "user";

export interface TeamSummary {
  id: string;
  name: string;
  slug: string;
}

export interface Membership {
  team: TeamSummary;
  role: Role;
}

export interface User {
  id: string;
  email: string;
  name: string;
  avatar_url: string | null;
  is_admin: boolean;
  pii_filter_enabled: boolean;
}

export interface MeResponse {
  user: User;
  memberships: Membership[];
}

export interface ApiErrorBody {
  error: {
    code: string;
    message: string;
    details?: unknown;
    request_id?: string | null;
  };
}

// --- Fase 1: Users / Teams / CompanyContext ---------------------------------

export interface UserDetail {
  id: string;
  email: string;
  name: string;
  avatar_url: string | null;
  is_admin: boolean;
  is_active: boolean;
  pii_filter_enabled: boolean;
}

export interface UserUpdate {
  name?: string;
  is_admin?: boolean;
  is_active?: boolean;
  pii_filter_enabled?: boolean;
}

export interface UserInvite {
  email: string;
  name: string;
  is_admin?: boolean;
  pii_filter_enabled?: boolean;
}

// --- Usuarios de servico (integracoes via API key) --------------------------

export interface ServiceUser {
  id: string;
  name: string;
  is_active: boolean;
  created_at: string;
}

export interface ServiceUserCreate {
  name: string;
}

export interface ServiceUserUpdate {
  name?: string;
  is_active?: boolean;
}

export interface ServiceUserKey {
  id: string;
  name: string;
  /** Prefixo de exibicao (ex.: orbit_sk_AbCd) -- nunca o valor completo. */
  key_prefix: string;
  is_active: boolean;
  last_used_at: string | null;
  expires_at: string | null;
  created_at: string;
}

export interface ServiceUserKeyCreate {
  name: string;
  expires_at?: string | null;
}

/** Retornado UMA UNICA VEZ na criacao -- inclui o valor em claro. */
export interface ServiceUserKeyCreated extends ServiceUserKey {
  key: string;
}

export interface ServiceUserTeam {
  team_id: string;
  team_name: string;
  team_slug: string;
  role: Role;
}

export interface ServiceUserTeamAdd {
  team_id: string;
  role?: Role;
}

/** Status do segredo de webhook -- nunca expoe o valor. */
export interface ServiceUserWebhookSecret {
  has_secret: boolean;
  created_at: string | null;
  rotated_at: string | null;
}

/** Retornado UMA UNICA VEZ ao gerar/rotacionar o segredo de webhook. */
export interface ServiceUserWebhookSecretCreated {
  secret: string;
}

/**
 * Uso agregado de um usuario (humano ou de servico) -- alimenta os big
 * numbers da conta de servico. ``thread_count``/``task_count`` sempre
 * renderizam mesmo com Langfuse desativado (custo zerado).
 */
export interface UserUsage {
  total_cost: number;
  total_requests: number;
  total_tokens: number;
  active_threads: number;
  cost_by_day: CostByDayItem[];
  cost_by_agent: MeCostByAgentItem[];
  expensive_threads: ExpensiveThreadItem[];
  thread_count: number;
  task_count: number;
}

export interface Team {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  is_active: boolean;
}

export interface TeamWithCount extends Team {
  member_count: number;
  manager_count: number;
  allowed_models_count: number;
}

export interface TeamCreate {
  name: string;
  slug?: string;
  description?: string;
  icon?: string;
  color?: string;
}

export interface TeamUpdate {
  name?: string;
  description?: string;
  icon?: string;
  color?: string;
}

export interface TeamMemberDetail {
  user_id: string;
  team_id: string;
  role: Role;
  user_email: string;
  user_name: string;
}

export interface CompanyContextData {
  content: string;
  updated_by_user_id: string | null;
  updated_at: string;
}

// --- Fase 2/3: Knowledge Bases / Documents ---------------------------------

export type KbPermission = "read" | "write" | "admin";

export type DocumentStatus = "pending" | "processing" | "indexed" | "failed";

export interface KnowledgeBase {
  id: string;
  name: string;
  description: string | null;
  short_description: string | null;
  owner_team_id: string;
  created_by_user_id: string;
  chunk_size: number;
  chunk_overlap: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface KnowledgeBaseWithCount extends KnowledgeBase {
  document_count: number;
  agent_count: number;
}

export interface KnowledgeBaseCreate {
  name: string;
  description?: string;
  short_description?: string;
  owner_team_id: string;
  chunk_size?: number;
  chunk_overlap?: number;
}

export interface KnowledgeBaseUpdate {
  name?: string;
  description?: string;
  short_description?: string;
  chunk_size?: number;
  chunk_overlap?: number;
  is_active?: boolean;
}

export interface KbPermissionRow {
  knowledge_base_id: string;
  team_id: string;
  team_name: string;
  permission: KbPermission;
  created_at: string;
}

export interface KbPermissionUpsert {
  team_id: string;
  permission: KbPermission;
}

export interface DocumentItem {
  id: string;
  knowledge_base_id: string;
  filename: string;
  description: string | null;
  mime_type: string;
  size_bytes: number;
  storage_path: string;
  status: DocumentStatus;
  error_message: string | null;
  uploaded_by_user_id: string;
  created_at: string;
  indexed_at: string | null;
  chunk_count: number | null;
}

export interface DocumentUpdate {
  description?: string | null;
}

export interface ChunkResult {
  chunk_id: string;
  document_id: string;
  document_filename: string;
  content: string;
  score: number;
  metadata: Record<string, unknown> | null;
}

export type KbSearchMode = "semantic" | "lexical";

export interface KbSearchIn {
  query: string;
  top_k?: number;
  mode?: KbSearchMode;
}

export interface KbSearchOut {
  query: string;
  mode?: KbSearchMode;
  results: ChunkResult[];
}

// --- M3 Fase 4: catalogo de LLMs --------------------------------------------

export type LlmProvider = "anthropic" | "openai" | "google" | "custom";

export type ReasoningEffort = "off" | "minimal" | "low" | "medium" | "high";

export interface LlmModel {
  id: string;
  provider: LlmProvider;
  model_id: string;
  display_name: string;
  description: string | null;
  is_active: boolean;
  supports_reasoning: boolean;
  supports_multimodal: boolean;
  supports_web_search: boolean;
  default_reasoning_effort: ReasoningEffort | null;
  max_output_tokens: number | null;
  created_at: string;
  updated_at: string;
}

export interface LlmModelCreate {
  provider: LlmProvider;
  model_id: string;
  display_name: string;
  description?: string;
  supports_reasoning?: boolean;
  supports_multimodal?: boolean;
  supports_web_search?: boolean;
  default_reasoning_effort?: ReasoningEffort | null;
  max_output_tokens?: number | null;
}

export interface LlmModelUpdate {
  display_name?: string;
  description?: string;
  is_active?: boolean;
  supports_reasoning?: boolean;
  supports_multimodal?: boolean;
  supports_web_search?: boolean;
  default_reasoning_effort?: ReasoningEffort | null;
  max_output_tokens?: number | null;
}

/**
 * M8: modelos permitidos para o usuario logado (interseccao dos
 * ``TeamAllowedModel`` dos times em que ele eh membro). Usado pelo
 * dropdown da aba /chat. ``is_default`` eh true se for default em
 * pelo menos 1 time.
 */
export interface MyAllowedModel {
  id: string;
  provider: LlmProvider;
  model_id: string;
  display_name: string;
  description: string | null;
  supports_reasoning: boolean;
  supports_multimodal: boolean;
  supports_web_search: boolean;
  default_reasoning_effort: ReasoningEffort | null;
  is_default: boolean;
  team_ids: string[];
}

export interface TeamAllowedModel {
  team_id: string;
  llm_model_id: string;
  is_default: boolean;
  provider: LlmProvider;
  model_id: string;
  display_name: string;
  description: string | null;
  is_active: boolean;
  supports_reasoning: boolean;
  supports_multimodal: boolean;
  supports_web_search: boolean;
  default_reasoning_effort: ReasoningEffort | null;
  created_at: string;
}

export interface TeamAllowedModelItem {
  llm_model_id: string;
  is_default: boolean;
}

export interface TeamAllowedModelsPut {
  items: TeamAllowedModelItem[];
}

// --- M3 Fase 5: MCP servers -------------------------------------------------

export type McpTransport = "stdio" | "http" | "sse";
export type McpHealthStatus = "ok" | "error";
export type McpPermissionLevel = "use";

export interface McpServer {
  id: string;
  name: string;
  description: string | null;
  owner_team_id: string;
  created_by_user_id: string;
  transport: McpTransport;
  url: string | null;
  command: string | null;
  args: unknown[] | null;
  env: Record<string, string> | null;
  enabled_tools: string[] | null;
  is_active: boolean;
  has_credentials: boolean;
  last_health_check_at: string | null;
  last_health_status: McpHealthStatus | null;
  created_at: string;
  updated_at: string;
}

export interface McpServerCreate {
  name: string;
  description?: string;
  owner_team_id: string;
  transport: McpTransport;
  url?: string;
  args?: unknown[];
  env?: Record<string, string>;
  credentials?: Record<string, unknown>;
  enabled_tools?: string[];
}

export interface McpServerUpdate {
  name?: string;
  description?: string;
  url?: string;
  args?: unknown[];
  env?: Record<string, string>;
  credentials?: Record<string, unknown>;
  enabled_tools?: string[];
  is_active?: boolean;
}

export interface McpServerPermission {
  mcp_server_id: string;
  team_id: string;
  team_name: string;
  permission: McpPermissionLevel;
  created_at: string;
}

export interface McpServerPermissionUpsert {
  team_id: string;
  permission?: McpPermissionLevel;
}

export interface McpTool {
  name: string;
  description: string | null;
  input_schema: Record<string, unknown> | null;
}

export interface McpToolsResponse {
  tools: McpTool[];
  cached: boolean;
}

// --- Skills (pacotes de instrucoes estilo SKILL.md) ------------------------

export interface Skill {
  id: string;
  name: string;
  description: string;
  instructions: string;
  owner_team_id: string;
  created_by_user_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface SkillCreate {
  name: string;
  description: string;
  instructions: string;
  owner_team_id: string;
}

export interface SkillUpdate {
  name?: string;
  description?: string;
  instructions?: string;
  is_active?: boolean;
}

export interface SkillPermission {
  skill_id: string;
  team_id: string;
  team_name: string;
  permission: "use";
  created_at: string;
}

export interface SkillPermissionUpsert {
  team_id: string;
  permission?: "use";
}

export interface SkillVersion {
  version: number;
  content: string;
  labels: string[];
  commit_message: string | null;
  created_at: string | null;
  metadata: Record<string, unknown>;
}

export interface AgentSkill {
  agent_id: string;
  skill_id: string;
  skill_name: string;
  skill_description: string;
}

export interface AgentSkillsPut {
  skill_ids: string[];
}

// --- M4 Fase 6: Agentes ----------------------------------------------------

export type AgentResponseTone =
  | "formal_technical"
  | "direct"
  | "didactic"
  | "casual";

export type AgentFallbackBehavior =
  | "inform_unknown"
  | "escalate_human"
  | "try_with_context";

export type AgentStatus =
  | "draft"
  | "pending_approval"
  | "published"
  | "archived";

export type AgentVisibility = "company" | "team_restricted" | "private";

export type ToolPermissionLevel = "auto" | "requires_approval" | "blocked";

export interface ToolPermissionConfig {
  level: ToolPermissionLevel;
}

export type ToolPermissions = Record<string, ToolPermissionConfig>;

/** Tipo do agente: nativo (runtime local) ou externo (proxy de bot HTTP). */
export type AgentType = "native" | "external";

export interface Agent {
  id: string;
  name: string;
  slug: string;
  agent_type: AgentType;
  description: string;
  icon: string | null;
  owner_team_id: string;
  created_by_user_id: string;
  system_prompt: string;
  response_tone: AgentResponseTone | null;
  response_language: string;
  fallback_behavior: AgentFallbackBehavior | null;
  memory_enabled: boolean;
  cite_sources: boolean;
  auto_escalate_low_confidence: boolean;
  /** null para agente externo (sem LLM local). */
  llm_model_id: string | null;
  reasoning_effort: ReasoningEffort | null;
  pii_filter_enabled: boolean;
  reports_enabled: boolean;
  web_search_enabled: boolean;
  status: AgentStatus;
  visibility: AgentVisibility;
  requires_install_approval: boolean;
  approval_responsible_user_id: string | null;
  created_at: string;
  updated_at: string;
  published_at: string | null;
}

export interface AgentDetail extends Agent {
  rating_avg: number | null;
  rating_count: number;
  kb_count: number;
  mcp_count: number;
}

export interface AgentCreate {
  name: string;
  slug?: string;
  agent_type?: AgentType;
  description: string;
  icon?: string;
  owner_team_id: string;
  /** Obrigatorios apenas para agente nativo. */
  system_prompt?: string;
  llm_model_id?: string;
  response_tone?: AgentResponseTone;
  response_language?: string;
  fallback_behavior?: AgentFallbackBehavior;
  memory_enabled?: boolean;
  cite_sources?: boolean;
  auto_escalate_low_confidence?: boolean;
  reasoning_effort?: ReasoningEffort | null;
  pii_filter_enabled?: boolean;
  reports_enabled?: boolean;
  web_search_enabled?: boolean;
  visibility?: AgentVisibility;
  requires_install_approval?: boolean;
  approval_responsible_user_id?: string;
}

export interface AgentUpdate {
  name?: string;
  slug?: string;
  description?: string;
  icon?: string;
  system_prompt?: string;
  llm_model_id?: string;
  response_tone?: AgentResponseTone;
  response_language?: string;
  fallback_behavior?: AgentFallbackBehavior;
  memory_enabled?: boolean;
  cite_sources?: boolean;
  auto_escalate_low_confidence?: boolean;
  reasoning_effort?: ReasoningEffort | null;
  pii_filter_enabled?: boolean;
  reports_enabled?: boolean;
  web_search_enabled?: boolean;
  requires_install_approval?: boolean;
  approval_responsible_user_id?: string;
  status?: AgentStatus;
}

export interface AgentOttoConfig {
  id: string;
  agent_id: string;
  enabled: boolean;
  funnel_prompt_text: string;
  funnel_prompt_label: string;
  funnel_steps: string[];
  tags_prompt_text: string;
  tags_prompt_label: string;
  tag_options: string[];
  followup_prompt_text: string;
  followup_prompt_label: string;
  created_at: string;
  updated_at: string;
}

export interface AgentOttoConfigPut {
  enabled: boolean;
  funnel_prompt_text: string;
  funnel_prompt_label: string;
  funnel_steps: string[];
  tags_prompt_text: string;
  tags_prompt_label: string;
  tag_options: string[];
  followup_prompt_text: string;
  followup_prompt_label: string;
}

// --- Conexao externa (agente proxy de bot HTTP remoto) ---------------------

export type AgentExternalAuthType = "none" | "api_key" | "hmac";
export type ExternalHealthStatus = "ok" | "error";

export interface AgentExternalConfig {
  id: string;
  agent_id: string;
  base_url: string;
  auth_type: AgentExternalAuthType;
  /** Read-side nunca expoe o segredo -- apenas a flag. */
  has_secret: boolean;
  timeout_seconds: number;
  langfuse_host: string | null;
  langfuse_public_key: string | null;
  has_langfuse_secret: boolean;
  health_status: ExternalHealthStatus | null;
  last_health_check_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgentExternalConfigPut {
  base_url: string;
  auth_type: AgentExternalAuthType;
  /** Write-only: null mantem o atual, "" limpa, valor substitui. */
  secret?: string | null;
  timeout_seconds: number;
  langfuse_host?: string | null;
  langfuse_public_key?: string | null;
  /** Write-only: null mantem o atual, "" limpa, valor substitui. */
  langfuse_secret?: string | null;
}

export interface ExternalConnectionTest {
  ok: boolean;
  latency_ms: number | null;
  error: string | null;
}

/** Tema/identidade visual dos relatorios do agente (build_report). */
export interface AgentReportTheme {
  id: string;
  agent_id: string;
  enabled: boolean;
  primary_color: string | null;
  accent_color: string | null;
  text_color: string | null;
  background_color: string | null;
  heading_font: string | null;
  body_font: string | null;
  chart_palette: string[];
  /** ID de um documento (imagem) de KB usado como logo no cabecalho. */
  logo_document_id: string | null;
  header_html: string | null;
  footer_html: string | null;
  /** ID de um documento (text/html) de KB usado como template proprio. */
  template_document_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgentReportThemePut {
  enabled: boolean;
  primary_color: string | null;
  accent_color: string | null;
  text_color: string | null;
  background_color: string | null;
  heading_font: string | null;
  body_font: string | null;
  chart_palette: string[];
  logo_document_id: string | null;
  header_html: string | null;
  footer_html: string | null;
  template_document_id: string | null;
}

export interface AgentKnowledgeBaseItem {
  agent_id: string;
  knowledge_base_id: string;
  knowledge_base_name: string;
  created_at: string;
}

export interface AgentKnowledgeBasesPut {
  knowledge_base_ids: string[];
}

export interface AgentMcpItem {
  agent_id: string;
  mcp_server_id: string;
  mcp_server_name: string;
  enabled_tools: string[] | null;
  tool_permissions: ToolPermissions;
}

export interface AgentMcpPutItem {
  mcp_server_id: string;
  enabled_tools: string[] | null;
  tool_permissions: ToolPermissions;
}

export interface AgentMcpsPut {
  items: AgentMcpPutItem[];
}

export interface AgentVisibilityTeamSummary {
  id: string;
  name: string;
  slug: string;
}

export interface AgentVisibilityUserSummary {
  id: string;
  name: string;
  email: string;
}

export interface AgentVisibilityData {
  mode: AgentVisibility;
  teams: AgentVisibilityTeamSummary[];
  users: AgentVisibilityUserSummary[];
}

export interface AgentVisibilityPut {
  mode: AgentVisibility;
  teams: string[];
  users: string[];
}

export interface AgentRating {
  agent_id: string;
  user_id: string;
  user_name: string;
  rating: number;
  comment: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgentRatingsResponse {
  items: AgentRating[];
  avg: number | null;
  count: number;
}

export interface AgentRatingPut {
  rating: number;
  comment?: string;
}

export interface AgentPreviewPromptIn {
  system_prompt?: string;
  response_tone?: AgentResponseTone;
  response_language?: string;
  fallback_behavior?: AgentFallbackBehavior;
  memory_enabled?: boolean;
  cite_sources?: boolean;
  auto_escalate_low_confidence?: boolean;
}

export interface PromptSection {
  name: string;
  label: string;
  content: string;
}

export interface AgentPreviewPromptOut {
  composed_prompt: string;
  sections: PromptSection[];
}

// --- M4 Fase 6: Templates de tom (admin) -----------------------------------

export interface PromptToneTemplate {
  key: string;
  name: string;
  content: string;
  created_at: string;
  updated_at: string;
}

export interface PromptToneTemplateUpdate {
  name?: string;
  content?: string;
}


// --- M5 Fase 7 + 9: Runtime de Agentes -------------------------------------

export type ThreadType = "direct" | "team" | "chat";

export type MessageRole = "user" | "assistant" | "system_event";

export type ExecutionStepType =
  | "agent_started"
  | "tool_call"
  | "tool_result"
  | "delegation"
  | "hitl_requested"
  | "hitl_resolved"
  | "agent_completed"
  | "error"
  | "thinking";

export type HitlStatus = "pending" | "approved" | "rejected" | "expired";

export interface Thread {
  id: string;
  type: ThreadType;
  user_id: string;
  agent_id: string | null;
  team_session_id: string | null;
  /** M8: modelo escolhido em chat direto. NULL em direct/team. */
  llm_model_id: string | null;
  title: string | null;
  is_sandbox: boolean;
  created_at: string;
  last_activity_at: string;
  pinned_at: string | null;
}

export interface ThreadDetail extends Thread {
  message_count: number;
}

export interface ThreadCreate {
  type: ThreadType;
  agent_id?: string;
  team_session_id?: string;
  /** M8: obrigatorio quando type=chat. */
  llm_model_id?: string;
  title?: string;
}

export interface ThreadUpdate {
  title?: string;
}

/** M8: PATCH /threads/{id}/llm-model. So vale para type=chat. */
export interface ThreadSetLlmModel {
  llm_model_id: string;
}

// --- M3 Anexos -------------------------------------------------------------

export type AttachmentKind = "input" | "output";

export type AttachmentDigestionStatus =
  | "not_needed"
  | "pending"
  | "processing"
  | "done"
  | "failed";

export interface Attachment {
  id: string;
  thread_id: string;
  message_id: string | null;
  execution_step_id: string | null;
  kind: AttachmentKind;
  filename: string;
  mime_type: string;
  size_bytes: number;
  digestion_status: AttachmentDigestionStatus;
  digestion_error: string | null;
  uploaded_by_user_id: string | null;
  created_by_agent_id: string | null;
  created_at: string;
}

export interface AttachmentList {
  items: Attachment[];
  count: number;
}

// Subset inline na Message (backend retorna eager-loaded).
export interface MessageAttachment {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  digestion_status: AttachmentDigestionStatus;
  digestion_error: string | null;
}

export interface Message {
  id: string;
  thread_id: string;
  role: MessageRole;
  content: string;
  agent_id: string | null;
  /**
   * Team mode: slug do subagente que produziu esta mensagem (resolvido de
   * ``agent.slug``). Cada subagente paralelo vira uma mensagem propria -> o
   * front reconstroi o header @subagente no reload. NULL no supervisor /
   * user / system_event.
   */
  agent_slug: string | null;
  /** M8: snapshot do modelo que respondeu esta msg (chat direto). */
  llm_model_id: string | null;
  langfuse_trace_id: string | null;
  created_at: string;
  attachments?: MessageAttachment[];
}

export interface MessageCreate {
  content: string;
  attachment_ids?: string[];
}

export interface ExecutionStep {
  id: string;
  thread_id: string;
  message_id: string | null;
  step_index: number;
  step_type: ExecutionStepType;
  agent_id: string | null;
  parent_step_id: string | null;
  payload: Record<string, unknown>;
  tokens_input: number | null;
  tokens_output: number | null;
  latency_ms: number | null;
  langfuse_observation_id: string | null;
  created_at: string;
}

export interface StepsResponse {
  items: ExecutionStep[];
  count: number;
}

export interface HitlRequest {
  id: string;
  thread_id: string;
  agent_id: string;
  tool_name: string;
  mcp_server_id: string | null;
  tool_input: Record<string, unknown>;
  reason: string | null;
  status: HitlStatus;
  decided_by_user_id: string | null;
  decision_comment: string | null;
  tool_input_override: Record<string, unknown> | null;
  langgraph_interrupt_id: string;
  created_at: string;
  decided_at: string | null;
  /** Preenchido quando a thread pertence a uma Task em background. */
  task_id?: string | null;
}

export interface HitlDecide {
  decision: "approve" | "reject";
  comment?: string;
  tool_input_override?: Record<string, unknown>;
}

// --- Tasks em background + Scheduler ----------------------------------------

export type TaskStatus =
  | "queued"
  | "running"
  | "waiting_decision"
  | "completed"
  | "error"
  | "token_limit"
  | "cancelled";

/** Status em que a execucao ainda avanca (polling/cancel habilitados). */
export const ACTIVE_TASK_STATUSES: readonly TaskStatus[] = [
  "queued",
  "running",
  "waiting_decision",
];

export interface Task {
  id: string;
  user_id: string;
  title: string;
  prompt: string;
  status: TaskStatus;
  thread_id: string;
  agent_id: string | null;
  agent_name: string | null;
  team_session_id: string | null;
  team_session_name: string | null;
  schedule_id: string | null;
  max_tokens: number | null;
  tokens_used: number;
  error_detail: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

export interface TaskCreate {
  title: string;
  prompt: string;
  agent_id?: string;
  team_session_id?: string;
  max_tokens?: number;
}

export type TaskScheduleKind = "once" | "cron";

export interface TaskSchedule {
  id: string;
  user_id: string;
  title: string;
  prompt: string;
  agent_id: string | null;
  agent_name: string | null;
  team_session_id: string | null;
  team_session_name: string | null;
  max_tokens: number | null;
  kind: TaskScheduleKind;
  cron_expression: string | null;
  run_at: string | null;
  timezone: string;
  enabled: boolean;
  next_run_at: string | null;
  last_run_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface TaskScheduleCreate {
  title: string;
  prompt: string;
  agent_id?: string;
  team_session_id?: string;
  max_tokens?: number;
  kind: TaskScheduleKind;
  cron_expression?: string;
  run_at?: string;
  timezone?: string;
}

export interface TaskScheduleUpdate {
  title?: string;
  prompt?: string;
  agent_id?: string | null;
  team_session_id?: string | null;
  max_tokens?: number | null;
  kind?: TaskScheduleKind;
  cron_expression?: string | null;
  run_at?: string | null;
  timezone?: string;
  enabled?: boolean;
}

export interface SchedulePreviewRequest {
  kind: TaskScheduleKind;
  cron_expression?: string;
  run_at?: string;
  timezone?: string;
}

export interface SchedulePreview {
  next_runs: string[];
}

// SSE event discriminated union — ESPELHA o backend (fonte canonica:
// sem-parar-orbit-backend/src/api/schemas/sse.py, SSE_EVENT_NAMES). Tipo de
// referencia/contrato do protocolo; os handlers (ChatStream/ChatPage/
// TeamChatView) fazem narrowing de `data: unknown`. Ao mudar um evento,
// atualize os dois lados (ha teste test_sse_contract no backend).
export type SseEvent =
  | { event: "agent_started"; data: { agent_id: string } }
  | { event: "agent_completed"; data: { agent_id: string } }
  | {
      event: "token";
      data: { content: string; agent_slug?: string | null; is_subagent?: boolean };
    }
  | {
      event: "thinking";
      data: { content: string; agent_slug?: string | null; is_subagent?: boolean };
    }
  | {
      event: "tool_call";
      data: {
        tool_call_id: string;
        tool_name: string;
        args: Record<string, unknown>;
        // Team mode: slug do agente que chamou a tool (null = supervisor).
        // Permite ao front fechar o balao SO desse agente em paralelo.
        agent_slug?: string | null;
        is_subagent?: boolean;
      };
    }
  | {
      event: "tool_result";
      data: {
        tool_call_id: string;
        output_preview: string;
        agent_slug?: string | null;
        is_subagent?: boolean;
      };
    }
  | {
      event: "delegation";
      data: {
        delegation_id: string;
        to_slug: string | null;
        to_agent_id: string | null;
        task_description: string | null;
      };
    }
  | {
      // Subagente terminou (tool ``task`` retornou) -> diagrama libera o no.
      event: "delegation_completed";
      data: { delegation_id: string; to_slug: string | null };
    }
  | {
      event: "todos_updated";
      data: { todos: Array<{ content: string; status: string }> };
    }
  | {
      event: "hitl_requested";
      data: {
        hitl_request_id: string;
        tool_name: string;
        tool_input: Record<string, unknown>;
        agent_id?: string | null;
      };
    }
  | {
      event: "hitl_resolved";
      data: {
        hitl_request_id: string;
        decision: "approve" | "reject";
        comment?: string | null;
      };
    }
  | { event: "message_completed"; data: { message_id: string; content: string } }
  | {
      event: "pii_detected";
      data: { direction: "input" | "output"; count: number; categories: string[] };
    }
  | { event: "cancelled"; data: { reason?: string } }
  | { event: "thread_titled"; data: { thread_id: string; title: string } }
  | {
      event: "artifact";
      data: {
        filename: string;
        mime_type: string;
        content_base64: string;
        size_bytes: number;
      };
    }
  | { event: "meta"; data: { run_id: string } }
  | {
      event: "error";
      data: {
        message?: string;
        reason?: string;
        direction?: string;
        categories?: string[];
      };
    }
  | { event: "done"; data: Record<string, never> };

// --- M6 Fase 8 + 10: Marketplace + Team Multi-Agente ----------------------

export type AgentRequestStatus = "pending" | "approved" | "rejected";

// Publish workflow
export interface AgentPublishRequest {
  id: string;
  agent_id: string;
  requested_by_user_id: string;
  status: AgentRequestStatus;
  reviewed_by_user_id: string | null;
  review_comment: string | null;
  created_at: string;
  reviewed_at: string | null;
  // Enriquecido pelos endpoints /me/pending-* (central de aprovações).
  agent_name?: string | null;
  agent_icon?: string | null;
  agent_description?: string | null;
  agent_owner_team_id?: string | null;
  agent_owner_team_name?: string | null;
  requested_by_name?: string | null;
}

export interface AgentPublishRequestCreate {
  justification?: string;
}

export interface AgentPublishDecide {
  decision: "approve" | "reject";
  comment?: string;
}

// Install workflow
export interface AgentInstallRequest {
  id: string;
  agent_id: string;
  requested_by_user_id: string;
  status: AgentRequestStatus;
  reviewed_by_user_id: string | null;
  review_comment: string | null;
  created_at: string;
  reviewed_at: string | null;
  // Enriquecido pelos endpoints /me/pending-* (central de aprovações).
  agent_name?: string | null;
  agent_icon?: string | null;
  agent_description?: string | null;
  agent_owner_team_id?: string | null;
  agent_owner_team_name?: string | null;
  requested_by_name?: string | null;
}

export interface AgentInstallDecide {
  decision: "approve" | "reject";
  comment?: string;
}

export interface AgentInstallResult {
  installed: boolean;
  request_id: string | null;
}

export interface MyAgent {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string | null;
  owner_team_id: string;
  installed_at: string;
  rating_avg: number | null;
}

// Marketplace
export type MarketplaceSort = "rating" | "installs" | "recent";

export interface MarketplaceAgent {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string | null;
  owner_team_id: string;
  created_by_user_id: string;
  published_at: string | null;
  rating_avg: number | null;
  rating_count: number;
  install_count: number;
  is_installed: boolean;
  visibility: string;
}

export interface MarketplaceList {
  items: MarketplaceAgent[];
  count: number;
  limit: number;
  offset: number;
}

export interface MarketplaceKbItem {
  id: string;
  name: string;
}

export interface MarketplaceMcpItem {
  id: string;
  name: string;
  enabled_tools: string[];
}

export interface MarketplaceAgentDetail extends MarketplaceAgent {
  requires_install_approval: boolean;
  kbs: MarketplaceKbItem[];
  mcps: MarketplaceMcpItem[];
}

// Team sessions
export interface TeamSessionAgentItem {
  agent_id: string;
  position: number;
  name: string;
  slug: string;
  description: string;
  icon: string | null;
}

export interface TeamSession {
  id: string;
  name: string;
  description: string | null;
  owner_user_id: string;
  supervisor_agent_id: string | null;
  supervisor_llm_model_id: string | null;
  instructions: string | null;
  max_delegations: number;
  created_at: string;
  updated_at: string;
}

export interface TeamSessionDetail extends TeamSession {
  agents: TeamSessionAgentItem[];
}

export interface TeamSessionCreate {
  name: string;
  description?: string;
  instructions?: string;
  supervisor_agent_id?: string;
  supervisor_llm_model_id?: string;
  max_delegations?: number;
  agent_ids: string[];
}

export interface TeamSessionUpdate {
  name?: string;
  description?: string;
  instructions?: string;
  supervisor_agent_id?: string | null;
  supervisor_llm_model_id?: string | null;
  max_delegations?: number;
}

export interface TeamSessionReplaceAgents {
  agent_ids: string[];
}

// Audit log viewer (admin) -- M7 polish: + actor_email/name + resource_name
export interface AuditLog {
  id: string;
  actor_user_id: string | null;
  actor_email: string | null;
  actor_name: string | null;
  action: string;
  resource_type: string;
  resource_id: string | null;
  resource_name: string | null;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  audit_metadata: Record<string, unknown> | null;
  created_at: string;
}

// M7 / BE-AUD2: cold storage archives
export interface AuditLogArchive {
  id: string;
  period_start: string;
  period_end: string;
  gcs_path: string;
  row_count: number;
  sha256: string;
  created_at: string;
}

export interface AuditLogArchiveList {
  items: AuditLogArchive[];
  total: number;
  limit: number;
  offset: number;
}

// --- M7 / Fase 11: PII ----------------------------------------------------

export type PiiAction = "mask" | "block" | "warn";
export type PiiDirection = "input" | "output";
export type PiiSource = "regex" | "llm";

export interface PiiFilterConfig {
  llm_model_id: string | null;
  detect_categories: string[];
  action: PiiAction;
  system_prompt: string | null;
  created_at: string;
  updated_at: string;
}

export interface PiiFilterConfigPatch {
  llm_model_id?: string | null;
  detect_categories?: string[];
  action?: PiiAction;
  system_prompt?: string | null;
}

export interface PiiDetection {
  id: string;
  thread_id: string;
  message_id: string | null;
  direction: PiiDirection;
  category: string;
  action: string;
  source: PiiSource;
  value_hint: string | null;
  created_at: string;
}

// --- M7 / Fase 12: Observabilidade ----------------------------------------

export interface ObservabilityStatus {
  enabled: boolean;
  reason?: string;
}

export interface ObservabilitySummary {
  total_cost: number;
  total_requests: number;
  total_tokens: number;
  active_threads: number;
}

export interface CostByDayItem {
  date: string;
  cost: number;
  requests: number;
  tokens: number;
}

export interface CostByModelItem {
  model: string;
  cost: number;
  share_pct: number;
  requests: number;
  tokens: number;
}

export interface CostByTeamItem {
  team_id: string;
  team_name: string;
  cost: number;
  requests: number;
  tokens: number;
}

export interface TopAgentItem {
  agent_id: string;
  agent_name: string;
  cost: number;
  requests: number;
  tokens: number;
  /** Agente externo: custo = total do projeto Langfuse do bot. */
  is_external?: boolean;
}

export interface UsageByUserItem {
  user_id: string;
  email: string | null;
  name: string | null;
  cost: number;
  requests: number;
  tokens: number;
}

export interface ExpensiveThreadItem {
  thread_id: string;
  title: string | null;
  cost: number;
}

export interface MeCostByAgentItem {
  agent_id: string;
  agent_name: string;
  cost: number;
  requests: number;
  tokens: number;
}

export interface MeUsage extends ObservabilitySummary {
  cost_by_day: CostByDayItem[];
  cost_by_agent: MeCostByAgentItem[];
  expensive_threads: ExpensiveThreadItem[];
}

export interface KnowledgeBaseSummary {
  total_knowledge_bases: number;
  total_documents: number;
  total_size_bytes: number;
  total_size_gb: number;
  connected_agents: number;
}

export interface McpSummary {
  total_mcp_servers: number;
  connected_agents: number;
}

export interface LlmCatalogCostItem {
  llm_model_id: string;
  provider: string;
  model_id: string;
  display_name: string;
  is_active: boolean;
  cost: number;
  requests: number;
  tokens: number;
  share_pct: number;
}

export interface ThreadMetricsModelEntry {
  model: string;
  requests: number;
  tokens: number;
}

export interface ThreadMetrics {
  thread_id: string;
  total_cost: number;
  total_tokens: number;
  total_requests: number;
  models: ThreadMetricsModelEntry[];
}

export type ThreadTodoStatus = "pending" | "in_progress" | "completed";

export interface ThreadTodoItem {
  content: string;
  status: ThreadTodoStatus;
}

export interface ThreadTodosResponse {
  todos: ThreadTodoItem[];
}

export interface ObservabilityFilters {
  from?: string;
  to?: string;
  team_id?: string;
  agent_id?: string;
  limit?: number;
  /** So usado por /observability/llm-catalog-costs (default false). */
  include_inactive?: boolean;
}

export interface AuditLogList {
  items: AuditLog[];
  total: number;
  limit: number;
  offset: number;
}

export interface AuditLogFilters {
  actor?: string;
  action?: string;
  resource_type?: string;
  resource_id?: string;
  since?: string;
  until?: string;
  limit?: number;
  offset?: number;
}
